/**
 * Client-side capability gating flag + shared decision logic.
 *
 * Wave 1 of the dynamic role-capability layer is behavior-neutral: everything
 * sits behind NEXT_PUBLIC_CAPABILITY_GATING (off by default). Flag off ⇒
 * every check ALLOWS (today's behavior, untouched); flag on ⇒ fail-closed —
 * a capability is allowed only if the user's effective set contains the key.
 *
 * NOTE: NEXT_PUBLIC_* is baked at build/startup, read via a function only so
 * tests can exercise both branches.
 */

/** JWT roles that bypass capability checks entirely — wave 6 (founder
 * decision 2026-08-24): ONLY `owner` (the full-bypass ceiling) and legacy
 * `admin` (the admin-secret persona / platform staff). `tenant_admin` is now
 * MATRIX-GOVERNED: its sessions resolve through the tenant's `tenant_admin`
 * system role's capability rows (role-resolved, NOT the user's membership
 * union — a founder who also holds the owner matrix role must still see the
 * Tenant Admin role's unticks take effect). The lockout guard in
 * lib/server/roles/roles-logic.ts keeps the user-management keys unremovable
 * from tenant_admin, so an admin can never lock themselves out of the matrix. */
export const CAPABILITY_BYPASS_ROLES: ReadonlySet<string> = new Set([
  'owner',
  'admin',
])

/** The one governed ADMIN-tier Hasura role: resolved via the tenant's
 * `tenant_admin` system role rows rather than user memberships. */
export const ROLE_GOVERNED_ADMIN_ROLE = 'tenant_admin'

/** Is client-side capability gating turned on? Off (default) = allow-all. */
export function isCapabilityGatingEnabled(): boolean {
  return process.env.NEXT_PUBLIC_CAPABILITY_GATING === '1'
}

/**
 * Pure decision: may a user holding `capabilitySet` use `key`?
 * Fail-closed — an absent set (still loading / query failed) denies.
 */
export function decideCapability(
  key: string,
  opts: {
    gatingEnabled: boolean
    bypass: boolean
    capabilitySet: ReadonlySet<string> | null
  }
): boolean {
  if (!opts.gatingEnabled) return true
  if (opts.bypass) return true
  if (!opts.capabilitySet) return false
  return opts.capabilitySet.has(key)
}
